import { useTranslation } from 'react-i18next';
import { Terminal, Download, Sparkles, HelpCircle } from 'lucide-react';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import WizardInstallSnippet from '@/components/wizard/WizardInstallSnippet';

const SKILL_ZIP_URL = '/skills/memara-memory/memara-memory.zip';

export default function WizardLanding() {
  const { t } = useTranslation('integrations');

  const faqItems = [
    { q: t('wizard.faq.mcpVsSkill.q'), a: t('wizard.faq.mcpVsSkill.a') },
    { q: t('wizard.faq.telemetry.q'), a: t('wizard.faq.telemetry.a') },
    { q: t('wizard.faq.cost.q'), a: t('wizard.faq.cost.a') },
  ];

  return (
    <div className="min-h-screen bg-white font-outer flex flex-col">
      <main className="flex-1">
        <section className="bg-gradient-to-b from-indigo-50 to-white pt-24 pb-16">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-6">
              <Sparkles className="w-8 h-8 text-indigo-600" aria-hidden />
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              {t('wizard.hero.headline')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8">
              {t('wizard.hero.subheadline')}
            </p>
            <WizardInstallSnippet variant="page" />
            <Button
              size="lg"
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={() => { window.location.href = '/login'; }}
            >
              {t('wizard.cta.getStarted')}
            </Button>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">
              {t('wizard.paths.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border border-gray-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Terminal className="w-5 h-5 text-indigo-600" />
                  <h3 className="text-xl font-semibold">{t('wizard.paths.cli.title')}</h3>
                </div>
                <ol className="list-decimal list-inside space-y-2 text-gray-600 mb-6">
                  <li>{t('wizard.paths.cli.step1')}</li>
                  <li>{t('wizard.paths.cli.step2')}</li>
                  <li>{t('wizard.paths.cli.step3')}</li>
                </ol>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" onClick={() => { window.location.href = '/integrations/cursor'; }}>
                    {t('wizard.paths.cli.cta')}
                  </Button>
                  <Button variant="outline" onClick={() => { window.location.href = '/integrations/claude-desktop'; }}>
                    {t('wizard.paths.cli.ctaClaude')}
                  </Button>
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Download className="w-5 h-5 text-indigo-600" />
                  <h3 className="text-xl font-semibold">{t('wizard.paths.zip.title')}</h3>
                </div>
                <p className="text-gray-600 mb-6">{t('wizard.paths.zip.description')}</p>
                <div className="flex flex-wrap gap-3">
                  <Button asChild>
                    <a href={SKILL_ZIP_URL} download="memara-memory.zip">
                      <Download className="w-4 h-4 mr-2" />
                      {t('wizard.paths.zip.download')}
                    </a>
                  </Button>
                  <Button variant="outline" onClick={() => { window.location.href = '/integrations/claude-desktop'; }}>
                    {t('wizard.paths.zip.cta')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <div className="flex items-center justify-center gap-2 mb-8">
              <HelpCircle className="w-6 h-6 text-indigo-600" />
              <h2 className="text-2xl font-bold text-gray-900">{t('wizard.faq.title')}</h2>
            </div>
            <dl className="space-y-6">
              {faqItems.map((item) => (
                <div key={item.q} className="bg-white rounded-lg p-5 border border-gray-200">
                  <dt className="font-semibold text-gray-900 mb-2">{item.q}</dt>
                  <dd className="text-gray-600">{item.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
